import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { getStatusChipStyles } from '../../../../utils/jobStatuses';
import {
  panelSurfaceSx,
  CLIENT_EARNINGS_STATUS_INFO,
} from './clientEarningsUtils';
import ClientEarningsInfoTooltip from './ClientEarningsInfoTooltip';

const StatusChip = ({ statusId, label, count, selected, onClick }) => {
  const styles = getStatusChipStyles(statusId);
  const accent = styles?.borderColor || '#94a3b8';

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        minWidth: 100,
        height: 40,
        px: 0.75,
        py: 0.5,
        borderRadius: '5px',
        border: '1px solid',
        borderColor: selected ? accent : 'divider',
        bgcolor: selected ? `${accent}14` : 'background.paper',
        cursor: 'pointer',
        textAlign: 'left',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ width: 5, alignSelf: 'stretch', borderRadius: '2px', bgcolor: accent }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, color: 'text.primary', lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}>
          {count}
        </Typography>
      </Box>
    </Box>
  );
};

export default function ClientJobsStatusChips({
  statusCounts = [],
  loading,
  error,
  selectedStatus,
  onStatusClick,
}) {
  const theme = useTheme();
  const visibleStatusCounts = useMemo(
    () => statusCounts.filter((item) => (item.count || 0) > 0),
    [statusCounts],
  );
  const totalJobs = visibleStatusCounts.reduce((sum, item) => sum + (item.count || 0), 0);

  return (
    <Box sx={panelSurfaceSx(theme)}>
      <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 500,
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Jobs Status
            <ClientEarningsInfoTooltip title={CLIENT_EARNINGS_STATUS_INFO} />
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {totalJobs} jobs
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>
        )}

        {!loading && !error && visibleStatusCounts.length === 0 && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', pb: 2 }}>
            No pipeline jobs for this client.
          </Typography>
        )}

        {!loading && !error && visibleStatusCounts.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              pb: 2,
            }}
          >
            {visibleStatusCounts.map((item) => (
              <StatusChip
                key={item.statusId}
                statusId={item.statusId}
                label={item.label}
                count={item.count || 0}
                selected={selectedStatus != null && Number(selectedStatus) === Number(item.statusId)}
                onClick={() => onStatusClick?.(item.statusId, item.label)}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
